import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';

type FolderRef = { id: string; name: string };
/** `GET /api/box/settings` (owner only): never the Client Secret — only whether one is saved, when, and why it cannot be used. */
export type BoxSettingsView = { clientId: string; enterpriseId: string; secretSet: boolean; savedAt: string | null; folder: FolderRef | null; error: string | null };
type BoxFolder = FolderRef & { path: FolderRef[]; folders: FolderRef[] };

const SETTINGS_KEY = ['box', 'settings'] as const;
const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const errorText = (e: unknown, fallback: string) => {
  if (!(e instanceof ApiError)) return fallback;
  const issues = fieldIssues(e);
  return issues.length > 0 ? issues.map((i) => i.message).join(' · ') : e.message;
};
/** Box calls its account's top level „All Files”. */
const folderName = (f: FolderRef) => (f.id === '0' ? 'Wszystkie pliki' : f.name);

/**
 * „Faktury w Box” in Ustawienia firmy, the owner's only (2026-11-03), like the GUS key: the Box Platform app's Client ID,
 * Enterprise ID and Client Secret (write-only), the app's e-mail to share the folder with, and the main folder —
 * chosen in a list of the folders the app sees or pasted as a link.
 */
export function BoxSettings() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api<BoxSettingsView>('/api/box/settings') });
  const view = settings.data;
  const account = useQuery({ queryKey: [...SETTINGS_KEY, 'account', view?.clientId, view?.enterpriseId, view?.savedAt], queryFn: () => api<{ login: string }>('/api/box/account'), enabled: !!view?.secretSet && !view.error, retry: false });
  const [form, setForm] = useState<{ clientId: string; enterpriseId: string; clientSecret: string } | null>(null);
  const [browsing, setBrowsing] = useState<string | null>(null);
  const [link, setLink] = useState('');
  const done = (v: BoxSettingsView) => {
    queryClient.setQueryData(SETTINGS_KEY, v);
    setForm(null);
    setBrowsing(null);
    setLink('');
  };
  const save = useMutation({ mutationFn: (body: { clientId: string; enterpriseId: string; clientSecret: string }) => api<BoxSettingsView>('/api/box/settings', { method: 'PUT', body }), onSuccess: done });
  const remove = useMutation({ mutationFn: () => api<BoxSettingsView>('/api/box/settings', { method: 'DELETE' }), onSuccess: done });
  const chooseFolder = useMutation({ mutationFn: (folder: string) => api<BoxSettingsView>('/api/box/settings/folder', { method: 'PUT', body: { folder } }), onSuccess: done });
  const folder = useQuery({ queryKey: [...SETTINGS_KEY, 'folders', browsing], queryFn: () => api<BoxFolder>(`/api/box/folders?parent=${encodeURIComponent(browsing!)}`), enabled: browsing !== null, retry: false });
  const busy = save.isPending || remove.isPending || chooseFolder.isPending;
  const failure = save.error ?? remove.error;
  const editing = view && (!view.secretSet || form !== null);
  const values = form ?? { clientId: '', enterpriseId: '', clientSecret: '' };

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate({ clientId: values.clientId.trim(), enterpriseId: values.enterpriseId.trim(), clientSecret: values.clientSecret.trim() });
  }
  const set = (k: keyof typeof values) => (e: { target: { value: string } }) => setForm({ ...values, [k]: e.target.value });

  return (
    <section className="settings-section" aria-label="Faktury w Box" data-feature-id="box.settings">
      <h2>Faktury w Box</h2>
      <p className="hint">PDF faktury przyjętej przez KSeF trafia sam do wybranego folderu w Box (podfoldery rok/miesiąc, faktury testowe do „Test”). Dane aplikacji Box widzi i zmienia tylko właściciel; Client Secret po zapisaniu jest zaszyfrowany i nikt go już nie zobaczy.</p>
      {settings.error && (
        <p className="alert" role="alert">
          {errorText(settings.error, 'Nie udało się wczytać ustawień Box')}
        </p>
      )}
      {view?.error && (
        <p className="alert" role="alert">
          {view.error}
        </p>
      )}
      {editing && (
        <form className="settings-form" onSubmit={submit}>
          <label className="field">
            <span>Client ID</span>
            <input value={values.clientId} onChange={set('clientId')} autoComplete="off" spellCheck={false} />
          </label>
          <label className="field">
            <span>Enterprise ID</span>
            <input value={values.enterpriseId} onChange={set('enterpriseId')} inputMode="numeric" autoComplete="off" spellCheck={false} />
          </label>
          <label className="field">
            <span>Client Secret</span>
            <input type="password" value={values.clientSecret} onChange={set('clientSecret')} autoComplete="new-password" spellCheck={false} placeholder={view.secretSet ? 'Zostaw puste, żeby nie zmieniać' : 'Wklej Client Secret aplikacji Box'} />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy}>
              {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
            </button>
            {view.secretSet && (
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setForm(null);
                  save.reset();
                }}
              >
                Anuluj
              </button>
            )}
          </div>
        </form>
      )}
      {view?.secretSet && !editing && (
        <>
          <p>
            <span>{`Client ID ${view.clientId} · Enterprise ID ${view.enterpriseId} · Client Secret zapisany ${view.savedAt ? WHEN.format(new Date(view.savedAt)) : ''}`.trim()}</span>{' '}
            <button className="link-button" type="button" onClick={() => setForm({ clientId: view.clientId, enterpriseId: view.enterpriseId, clientSecret: '' })}>
              Zmień dane
            </button>{' '}
            <button className="link-button" type="button" disabled={busy} onClick={() => remove.mutate()}>
              Usuń
            </button>
          </p>
          {account.data && (
            <p>
              E-mail konta aplikacji: <strong>{account.data.login}</strong>
              <br />
              <span className="muted">Udostępnij mu folder na faktury w Box (Zaproś współpracowników, rola Edytor).</span>
            </p>
          )}
          {account.error && (
            <p className="alert" role="alert">
              {errorText(account.error, 'Nie udało się połączyć z Box')}
            </p>
          )}
          <p>
            <span>{`Folder na faktury: ${view.folder ? view.folder.name : 'nie wybrano'}`}</span>{' '}
            {browsing === null && (
              <button className="link-button" type="button" onClick={() => setBrowsing(view.folder?.id ?? '0')}>
                Wybierz folder
              </button>
            )}
          </p>
          {browsing !== null && (
            <div className="settings-form">
              {folder.error && (
                <p className="alert" role="alert">
                  {errorText(folder.error, 'Nie udało się wczytać folderów z Box')}
                </p>
              )}
              {folder.data && (
                <>
                  <p>
                    {[...folder.data.path, folder.data].map((f, i, all) =>
                      i < all.length - 1 ? (
                        <span key={f.id}>
                          <button className="link-button" type="button" onClick={() => setBrowsing(f.id)}>
                            {folderName(f)}
                          </button>
                          {' / '}
                        </span>
                      ) : (
                        <strong key={f.id}>{folderName(f)}</strong>
                      ),
                    )}
                  </p>
                  {folder.data.folders.length === 0 ? (
                    <p className="muted">{folder.data.id === '0' ? 'Aplikacja nie widzi żadnego folderu – udostępnij jej folder w Box.' : 'Brak podfolderów.'}</p>
                  ) : (
                    <ul className="settings-list">
                      {folder.data.folders.map((f) => (
                        <li key={f.id} className="settings-row">
                          <button className="link-button" type="button" onClick={() => setBrowsing(f.id)}>
                            {f.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="actions">
                    <button className="btn primary" type="button" disabled={busy || folder.data.id === '0'} onClick={() => chooseFolder.mutate(folder.data!.id)}>
                      Wybierz ten folder
                    </button>
                  </div>
                </>
              )}
              <div className="field-row">
                <label className="field grow">
                  <span>Link do folderu w Box</span>
                  <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://….box.com/folder/123…" spellCheck={false} />
                </label>
                <div className="actions">
                  <button className="btn" type="button" disabled={busy || link.trim() === ''} onClick={() => chooseFolder.mutate(link.trim())}>
                    Zapisz folder
                  </button>
                </div>
              </div>
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setBrowsing(null);
                  chooseFolder.reset();
                }}
              >
                Anuluj
              </button>
              {chooseFolder.error && (
                <p className="alert" role="alert">
                  {errorText(chooseFolder.error, 'Nie udało się zapisać folderu')}
                </p>
              )}
            </div>
          )}
        </>
      )}
      {failure && (
        <p className="alert" role="alert">
          {errorText(failure, 'Nie udało się zapisać ustawień Box')}
        </p>
      )}
    </section>
  );
}
